import {MAX_GETTY_IMAGES_PER_EMAIL, MAX_GETTY_VIDEOS_PER_EMAIL, MULTI_MEDIA_SOURCE_GETTY} from '@Libraries/user-media-library';
import type {Component, Editor} from 'grapesjs';

interface CanAddUniqueGettyAssetOpts {
  mediaType: string;
  candidateHashedFilename: string;
  excludeComponent?: Component;
}

const pendingGettyAddsByMediaType = new Map<string, Map<string, number>>();

export function reserveGettyAsset(mediaType: string, hashedFilename: string): void {
  const pendingAdds = pendingGettyAddsByMediaType.get(mediaType) ?? new Map<string, number>();
  pendingAdds.set(hashedFilename, (pendingAdds.get(hashedFilename) ?? 0) + 1);
  pendingGettyAddsByMediaType.set(mediaType, pendingAdds);
}

export function releaseGettyAsset(mediaType: string, hashedFilename: string): void {
  const pendingAdds = pendingGettyAddsByMediaType.get(mediaType);
  if (!pendingAdds) {
    return;
  }

  const count = pendingAdds.get(hashedFilename) ?? 0;
  if (count <= 1) {
    pendingAdds.delete(hashedFilename);
  } else {
    pendingAdds.set(hashedFilename, count - 1);
  }
}

export function canAddUniqueGettyAsset(grapesjsEditor: Editor, opts: CanAddUniqueGettyAssetOpts): boolean {
  const usedHashedFilenames = getUniqueGettyHashedFilenames(grapesjsEditor, opts.mediaType, opts.excludeComponent);
  for (const hashedFilename of pendingGettyAddsByMediaType.get(opts.mediaType)?.keys() ?? []) {
    usedHashedFilenames.add(hashedFilename);
  }

  return usedHashedFilenames.has(opts.candidateHashedFilename) || usedHashedFilenames.size < getGettyLimitForMediaType(opts.mediaType);
}

export function warnIfGettyLimitsExceeded(grapesjsEditor: Editor): void {
  for (const mediaType of [window.PMW.emails.mediaType.IMAGE, window.PMW.emails.mediaType.VIDEO]) {
    if (getUniqueGettyHashedFilenames(grapesjsEditor, mediaType).size > getGettyLimitForMediaType(mediaType)) {
      grapesjsEditor.runCommand(window.PMW.emails.commands.SHOW_GETTY_LIMIT_MESSAGE, {type: mediaType});
    }
  }
}

export function isEmailContentMounted(grapesjsEditor: Editor): boolean {
  const wrapper = grapesjsEditor.DomComponents.getWrapper();
  const mainContent = wrapper?.find(`[data-gjs-type="${window.PMW.emails.components.container.PMW_CONTENT}"]`)[0];

  return mainContent?.getEl()?.isConnected === true;
}

export function getUniqueGettyHashedFilenames(grapesjsEditor: Editor, mediaType: string, excludeComponent?: Component): Set<string> {
  const hashedFilenames = new Set<string>();
  const wrapper = grapesjsEditor.DomComponents.getWrapper();

  if (!wrapper) {
    return hashedFilenames;
  }

  forEachComponent(wrapper, (component) => {
    if (component === excludeComponent || !isGettyMediaOfType(grapesjsEditor, component, mediaType)) {
      return;
    }

    const hashedFilename = component.get(window.PMW.emails.props.HASHED_FILENAME) as string;
    if (hashedFilename) {
      hashedFilenames.add(hashedFilename);
    }
  });

  return hashedFilenames;
}

function getGettyLimitForMediaType(mediaType: string): number {
  return mediaType === window.PMW.emails.mediaType.VIDEO ? MAX_GETTY_VIDEOS_PER_EMAIL : MAX_GETTY_IMAGES_PER_EMAIL;
}

function forEachComponent(component: Component, callback: (component: Component) => void): void {
  callback(component);
  component.components().each((child: Component) => {
    forEachComponent(child, callback);
  });
}

function isGettyMediaOfType(grapesjsEditor: Editor, component: Component, mediaType: string): boolean {
  if (component.get(window.PMW.emails.props.CONTENT_SOURCE) !== MULTI_MEDIA_SOURCE_GETTY) {
    return false;
  }

  const backgroundImage = component.getStyle()['background-image'];
  const hasBackgroundImage = typeof backgroundImage !== 'undefined' && backgroundImage !== 'none';
  const hasGraphic =
    component.get('type') === window.PMW.emails.components.graphic.PMW_IMAGE || component.get('type') === window.PMW.emails.components.graphic.PMW_VIDEO || hasBackgroundImage;

  if (!hasGraphic) {
    return false;
  }

  return (grapesjsEditor.runCommand(window.PMW.emails.commands.GET_COMPONENT_MEDIA_TYPE, {component}) as string) === mediaType;
}
